// FLIP spring reflow for SortableGrid — the physicality layer. Whenever the
// live placement map changes (drag preview, resize preview, commit, tidy, undo,
// server-driven change), displaced tiles spring from their previous position to
// the new one instead of teleporting.
//
// Position AND size ride the same spring. A tile that changes row span — a
// derived-height container measuring a body that just grew — animates its
// height on the identical curve the displaced tiles below it animate their
// translate on, so its bottom edge and their top edges stay locked together for
// the whole flight. Springing position alone puts the followers INSIDE the
// grown tile for the length of the animation: the tile snaps open in one frame
// and its neighbours slide down through it.
//
// Engine: the Web Animations API — nothing framework-specific.
// `node.animate()` never touches React state or the element's inline `style`,
// so it cannot fight React's own style writes, and a finished animation is
// self-cleaning (keyframes end at the identity transform with no `fill`).
// The springy feel comes from a damped spring numerically sampled ONCE at
// module load into a keyframe curve — works in every WAAPI browser, no
// reliance on the newer `linear()` easing.
//
// Design notes (verified against react-flip-toolkit and @formkit/auto-animate
// source):
//   - INTERRUPTION: measure the mid-flight visual position BEFORE cancelling
//     the running animation (the computed transform still reflects it), fold
//     the residual into the new delta, THEN cancel and start the redirected
//     spring. Cancel-first implementations visibly pop under the rapid
//     retriggering a live drag produces.
//   - BATCHING: one read pass over every tile (offsets + residual transforms),
//     then one write pass (cancel + animate). Interleaving reads and writes
//     invalidates layout per tile — O(N) thrash on large dashboards.
//   - Offsets (`offsetLeft`/`offsetTop`) are relative to the grid's
//     `position: relative` container — immune to page/container scroll between
//     reflows, unlike viewport rects.
//   - Sub-0.5px deltas are skipped (rounding noise floods animations when
//     reflows fire many times per second).

import * as React from 'react';
import { forcedReducedMotion } from '../../lib/interaction-preferences';

import type { GridLayoutItem } from './grid-layout';

export interface FlipRegistryEntry {
  node: HTMLElement;
  /** Translate spring (displacement). */
  anim: Animation | null;
  /** Height spring (row-span growth). Separate from `anim` because the two are
   *  independent — a tile moves without resizing and resizes without moving —
   *  and each needs its own mid-flight read on interruption. */
  sizeAnim: Animation | null;
}

// Damped spring x: 0 → 1 (stiffness 300, damping 28, mass 1 → ζ ≈ 0.81:
// slight overshoot and settle — "an object with mass being pushed"). Sampled
// with semi-implicit Euler at 1ms substeps (the fixed-substep approach the
// rebound spring engine uses), recorded at 60Hz until settled.
function sampleSpring(
  stiffness: number,
  damping: number,
  mass: number,
): { samples: number[]; durationMs: number } {
  const substep = 0.001;
  const samplePeriod = 1 / 60;
  const samples: number[] = [0];
  let x = 0;
  let v = 0;
  let t = 0;
  let nextSample = samplePeriod;
  while (t < 5) {
    const accel = (-stiffness * (x - 1) - damping * v) / mass;
    v += accel * substep;
    x += v * substep;
    t += substep;
    if (t >= nextSample) {
      samples.push(x);
      nextSample += samplePeriod;
    }
    if (Math.abs(1 - x) < 0.001 && Math.abs(v) < 0.001) break;
  }
  // Land EXACTLY on identity so no residual sub-pixel transform survives.
  samples.push(1);
  return { samples, durationMs: t * 1000 };
}

const SPRING = sampleSpring(300, 28, 1);

/** Keyframes springing a tile from a (dx, dy) displacement back to identity.
 *  Samples are uniform-interval, so WAAPI's even keyframe distribution (with
 *  its default linear easing between frames) reproduces the curve exactly. */
export function springKeyframes(dx: number, dy: number): Keyframe[] {
  return SPRING.samples.map((p) => ({
    transform: `translate(${dx * (1 - p)}px, ${dy * (1 - p)}px)`,
  }));
}

/** Keyframes springing a tile's height from `from` to `to`. Same sample array
 *  as `springKeyframes`, so a growing tile and the tiles it displaces are at
 *  the same phase on every frame. An explicit height overrides a grid item's
 *  stretch, and the animation carries no `fill`, so the tile is back to its
 *  grid-derived height the moment it finishes. */
export function springHeightKeyframes(from: number, to: number): Keyframe[] {
  return SPRING.samples.map((p) => ({ height: `${from + (to - from) * p}px` }));
}

/** Cancel whatever spring `running` is, then start `keyframes` in its place —
 *  the whole write half of a FLIP pass, for translate and for height alike.
 *  Returns the handle to store back on the entry, so a cancelled spring can
 *  never be left behind as a live-looking handle. `keyframes` of null means
 *  "cancel only": a no-op delta, reduced motion, or a tile the user just
 *  grabbed, all of which cancel and start nothing. */
function restartSpring(
  node: HTMLElement,
  running: Animation | null,
  keyframes: Keyframe[] | null,
): Animation | null {
  if (running) {
    try {
      running.cancel();
    } catch {
      // cancel() can throw on detached documents
    }
  }
  if (!keyframes) return null;
  return node.animate(keyframes, { duration: SPRING.durationMs });
}

// One MediaQueryList for the session. `matches` is live, so re-reading it is
// enough — there is no listener because FLIP asks at the moment it animates.
// A forced preference on the document (`applyMotionPreference`) wins over it.
let reducedMotionQuery: MediaQueryList | null = null;
function prefersReducedMotion(): boolean {
  const forced = forcedReducedMotion();
  if (forced !== null) return forced;
  reducedMotionQuery ??= window.matchMedia('(prefers-reduced-motion: reduce)');
  return reducedMotionQuery.matches;
}

/** Current translate of a running FLIP animation, read from the computed
 *  transform (our animations are translate-only, so the matrix is exact). */
function residualTranslate(node: HTMLElement): { x: number; y: number } {
  const transform = getComputedStyle(node).transform;
  if (!transform || transform === 'none') return { x: 0, y: 0 };
  const matrix = new DOMMatrix(transform);
  return { x: matrix.m41, y: matrix.m42 };
}

export function useFlipReflow(options: {
  /** id → { node, anim } for every mounted tile (owned by the grid shell). */
  registry: React.MutableRefObject<Map<string, FlipRegistryEntry>>;
  /** Live (preview-aware) placement — the effect keys on it, so every reflow
   *  source funnels through here. */
  placementMap: Map<string, GridLayoutItem>;
  /** Active gesture tile (never springs — it follows the pointer/ghost). */
  activeId: string | null;
  /** Mobile stack — no grid placement, no FLIP. */
  singleColumn: boolean;
  /** Row cell height in px — with `gapPx`, turns a row span into the tile's
   *  settled pixel height WITHOUT reading the DOM. A measured read cannot serve
   *  here: by the time this layout effect runs the new span is already applied,
   *  so the element reports its destination, not its origin. */
  rowHeight: number;
  /** Row/column gap in px. */
  gapPx: number;
  /** One-shot suppression: a host that commits a NON-gesture placement change
   *  (content settling into its measured height on load) sets `.current`
   *  before the commit; the pass that consumes it snaps — offsets are
   *  recorded, no spring plays — and clears the flag. */
  snapRef?: React.MutableRefObject<boolean> | undefined;
}) {
  const { registry, placementMap, activeId, singleColumn, rowHeight, gapPx, snapRef } = options;
  const prevOffsets = React.useRef(new Map<string, { left: number; top: number }>());
  // Per tile: the column span and the settled pixel height of the LAST pass —
  // the origin of a height spring, and the guard that keeps one from firing.
  const prevSpans = React.useRef(new Map<string, { w: number; px: number }>());
  // The tile active in the PREVIOUS pass also skips one pass: on drop it jumps
  // from translate-follow to its committed cell in the same frame the preview
  // merges into layout — springing it would replay the whole drag.
  const prevActiveRef = React.useRef<string | null>(null);

  React.useLayoutEffect(() => {
    if (singleColumn) {
      prevOffsets.current.clear();
      prevSpans.current.clear();
      prevActiveRef.current = null;
      return;
    }

    const skipIds = new Set(
      [activeId, prevActiveRef.current].filter((id): id is string => id != null),
    );
    prevActiveRef.current = activeId;
    // A snap pass behaves exactly like a reduced-motion pass: everything is
    // measured and recorded, running springs are cancelled, no new ones start.
    const snap = snapRef?.current === true;
    if (snapRef) snapRef.current = false;
    const reduced = snap || prefersReducedMotion();

    // Pass 1 — reads only. Residuals must be read BEFORE any cancel() below:
    // cancelling snaps the computed transform to identity and discards the
    // mid-flight position.
    const nextOffsets = new Map<string, { left: number; top: number }>();
    const nextSpans = new Map<string, { w: number; px: number }>();
    const moved: Array<{ entry: FlipRegistryEntry; dx: number; dy: number }> = [];
    const grown: Array<{ entry: FlipRegistryEntry; from: number; to: number }> = [];
    for (const [id, entry] of registry.current) {
      const { node } = entry;
      if (!node.isConnected) continue;
      const left = node.offsetLeft;
      const top = node.offsetTop;
      nextOffsets.set(id, { left, top });
      const span = placementMap.get(id);
      // Settled height of the span, derived not measured — see `rowHeight`.
      const spanPx = span ? span.h * rowHeight + (span.h - 1) * gapPx : 0;
      const prevSpan = prevSpans.current.get(id);
      if (span) nextSpans.set(id, { w: span.w, px: spanPx });
      if (skipIds.has(id)) {
        // A grabbed tile must drop any in-flight spring NOW — a running WAAPI
        // transform overrides the drag's pointer-follow translate, and a height
        // spring fights a live resize's row-by-row preview. Both pushes are
        // unconditional: a zero delta cancels and starts nothing, which is
        // exactly what a tile with no spring running needs done to it.
        moved.push({ entry, dx: 0, dy: 0 }); // cancel-only
        grown.push({ entry, from: spanPx, to: spanPx }); // cancel-only
        continue;
      }
      // A running height spring already owns the element's used height, so its
      // mid-flight value IS the visual origin. With none running the element
      // reports the destination — the previous pass's span is the only record
      // of where it came from, and a tile that has no previous pass springs
      // from where it already is.
      const settled = prevSpan?.px ?? spanPx;
      const from = entry.sizeAnim?.playState === 'running' ? node.offsetHeight : settled;
      // Only a pure row-span change is a disclosure. A column-span change means
      // the whole tile was re-laid-out (a resize commit, a responsive column
      // count), where a height spring reads as the layout wobbling — so those
      // arrive already at rest and spring nothing.
      grown.push({ entry, from: prevSpan?.w === span?.w ? from : spanPx, to: spanPx });
      const prev = prevOffsets.current.get(id);
      if (!prev) continue; // newly mounted — appear in place
      let dx = prev.left - left;
      let dy = prev.top - top;
      if (entry.anim && entry.anim.playState === 'running') {
        const residual = residualTranslate(node);
        dx += residual.x;
        dy += residual.y;
      }
      if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) continue;
      moved.push({ entry, dx, dy });
    }
    prevOffsets.current = nextOffsets;
    prevSpans.current = nextSpans;

    // Pass 2 — writes only. Sub-pixel deltas are not worth a spring: they read
    // as a flicker and they cancel a real one already in flight.
    const still = (delta: number) => Math.abs(delta) < 0.5;
    for (const { entry, dx, dy } of moved) {
      const flight = reduced || (still(dx) && still(dy)) ? null : springKeyframes(dx, dy);
      entry.anim = restartSpring(entry.node, entry.anim, flight);
    }
    for (const { entry, from, to } of grown) {
      const flight = reduced || still(to - from) ? null : springHeightKeyframes(from, to);
      entry.sizeAnim = restartSpring(entry.node, entry.sizeAnim, flight);
    }
  }, [registry, placementMap, activeId, singleColumn, rowHeight, gapPx, snapRef]);
}
